import Menu from '@/global/components/MenuV2'
import { cn } from '@/lib/utils'
import localeText from '@/locales/datagrid'
import { Alert, IconButton, Select } from '@mui/material'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ReactNode } from 'react'
import { useTableApiContext } from '../../utils/table-context'

export function FooterAviso({
    children,
    severity = 'warning',
}: {
    children: ReactNode
    severity?: 'info' | 'warning' | 'error' | 'success'
}) {
    return (
        <Alert
            severity={severity}
            className="md:max-w-7/10 max-w-1/2 max-h-18 overflow-y-auto select-none"
        >
            {children}
        </Alert>
    )
}

interface ITableFooterProps {
    children?: React.ReactNode
}

export function TableFooter({ children }: ITableFooterProps) {
    return (
        <>
            {children}
            <Selection />
            <div className="flex flex-row items-center gap-4 text-sm select-none text-primary">
                <RowsPerPage />
                <Exibindo />
                <Pagination />
            </div>
        </>
    )
}

function RowsPerPage() {
    const { pageSizeOptions, pagination, setPageSize } = useTableApiContext()

    if (!pageSizeOptions?.length) return null

    return (
        <div className="grid grid-rows-2 mr-2">
            <span className="whitespace-nowrap">
                {localeText.footerRowsPerPage}
            </span>
            <Select
                value={pagination.pageSize}
                onChange={e =>
                    setPageSize(
                        typeof e.target.value === 'number'
                            ? e.target.value
                            : parseInt(e.target.value, 10),
                    )
                }
                className={cn(
                    'h-5 p-0 overflow-hidden w-fit',
                    'outline-none! border-0 shadow-none',
                    'focus:border-0 focus:shadow-none focus:ring-0',
                )}
                sx={{
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '& .MuiSelect-select': {
                        padding: 0,
                    },
                    boxShadow: 'none',
                    border: 'none',
                    '&. MuiInputBase-root': {
                        width: 'fit-content',
                    },
                }}
            >
                {pageSizeOptions.map((option, index) => (
                    <Menu.Item
                        key={index}
                        value={
                            typeof option === 'number' ? option : option.value
                        }
                        className="text-sm text-primary"
                    >
                        {typeof option === 'number'
                            ? option
                            : option.label || option.value}
                    </Menu.Item>
                ))}
            </Select>
        </div>
    )
}

function Selection() {
    const { selectionModel } = useTableApiContext()

    return (
        <div className="flex-1 text-sm text-foreground">
            {localeText.footerRowSelected(selectionModel.length)}
        </div>
    )
}

function Pagination() {
    const { pagination, table, setPagination } = useTableApiContext()

    if (!table) return null

    const pageCount = Math.max(table.getPageCount(), 1),
        canNextPage = table.getCanNextPage(),
        canPreviousPage = table.getCanPreviousPage()

    function nextPage() {
        if (canNextPage) {
            setPagination(prev => ({
                ...prev,
                pageIndex: prev.pageIndex + 1,
            }))
        }
    }

    function previousPage() {
        if (canPreviousPage) {
            setPagination(prev => ({
                ...prev,
                pageIndex: prev.pageIndex - 1,
            }))
        }
    }

    return (
        <div className="flex flex-row items-center gap-2">
            <span className="grid grid-rows-2">
                {localeText
                    .paginationLabelPageXofY({
                        index: pagination.pageIndex + 1,
                        pages: pageCount,
                    })
                    .map((text, index) => (
                        <span key={index}>{text}</span>
                    ))}
            </span>
            <div className="flex items-center gap-1 *:enabled:text-primary">
                <IconButton
                    title={localeText.previous}
                    onClick={previousPage}
                    disabled={!canPreviousPage}
                >
                    <ChevronLeft className="size-5" />
                </IconButton>
                <IconButton
                    title={localeText.next}
                    onClick={nextPage}
                    disabled={!canNextPage}
                >
                    <ChevronRight className="size-5" />
                </IconButton>
            </div>
        </div>
    )
}

function Exibindo() {
    const { pagination, table } = useTableApiContext()

    if (!table) return null

    const from = pagination.pageIndex * pagination.pageSize + 1,
        max = table.getFilteredRowModel().rows.length,
        to =
            (pagination.pageIndex + 1) * pagination.pageSize > max
                ? max
                : (pagination.pageIndex + 1) * pagination.pageSize

    return (
        <span
            className="grid grid-rows-2 w-28"
            title={`Total de linhas: ${max}`}
        >
            {localeText.footerShowingRows(from, to, max).map((text, index) => (
                <span className="truncate" key={index}>
                    {text}
                </span>
            ))}
        </span>
    )
}

interface ChildProp extends ITableFooterProps {
    children?: React.ReactNode
    className?: string
    hidden?: boolean
}

export function TableFooterContainer({
    children,
    className,
    hidden = false,
}: ChildProp) {
    if (hidden) return null
    return (
        <div
            className={cn(
                'w-full flex overflow-x-auto scrollbar-thin',
                'border-x border-b rounded-b-md',
                'space-x-2 p-2 max-h-20',
            )}
        >
            <div
                className={cn(
                    'flex items-center justify-end flex-1',
                    className,
                )}
            >
                {children}
            </div>
        </div>
    )
}
